<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { WORKSPACE_LIST_RESIZE_LEVELS } from 'dashboard/composables/useWorkbenchLayout';

const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
  },
  maxWidth: { type: Number, default: 480 },
  isHidden: {
    type: Boolean,
    default: false,
  },
  isDragging: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectWidth', 'hide', 'startDrag', 'cancelDrag']);

const { t } = useI18n();
const rootRef = ref(null);
const triggerRef = ref(null);
const firstLevelRef = ref(null);
const isMenuOpen = ref(false);

let gestureStartX = null;
let gestureMoved = false;
let suppressClickUntil = 0;

const currentLevelIndex = computed(() => {
  const index = WORKSPACE_LIST_RESIZE_LEVELS.findIndex(
    level => level.width === props.modelValue
  );
  return index < 0 ? 0 : index;
});

const currentLevel = computed(
  () => WORKSPACE_LIST_RESIZE_LEVELS[currentLevelIndex.value]
);

const levelLabel = level => {
  const labels = {
    icons: t('WORKSPACE_RESIZE.LEVELS.ICONS'),
    compact: t('WORKSPACE_RESIZE.LEVELS.COMPACT'),
    comfortable: t('WORKSPACE_RESIZE.LEVELS.COMFORTABLE'),
    wide: t('WORKSPACE_RESIZE.LEVELS.WIDE'),
  };
  return labels[level.key];
};

const currentValueText = computed(() => {
  if (props.isHidden) return t('WORKSPACE_RESIZE.HIDDEN');
  return `${levelLabel(currentLevel.value)}, ${t(
    'WORKSPACE_RESIZE.WIDTH_VALUE',
    { width: currentLevel.value.width }
  )}`;
});

const closeMenu = ({ restoreFocus = false } = {}) => {
  if (!isMenuOpen.value) return;
  isMenuOpen.value = false;
  if (restoreFocus) nextTick(() => triggerRef.value?.focus());
};

const openMenu = () => {
  isMenuOpen.value = true;
  nextTick(() => firstLevelRef.value?.focus());
};

const toggleMenu = () => {
  if (Date.now() < suppressClickUntil) return;
  if (isMenuOpen.value) {
    closeMenu();
    return;
  }
  openMenu();
};

const selectWidth = width => {
  if (width > props.maxWidth) return;
  emit('selectWidth', width);
  closeMenu({ restoreFocus: true });
};

const hideList = () => {
  emit('hide');
  closeMenu({ restoreFocus: true });
};

const setFirstLevelRef = (element, index) => {
  if (index === 0) firstLevelRef.value = element;
};

const clientX = event =>
  event.touches?.[0]?.clientX ??
  event.changedTouches?.[0]?.clientX ??
  event.clientX;

let removeGestureListeners = () => {};

const trackGesture = event => {
  if (gestureStartX == null) return;
  if (Math.abs(clientX(event) - gestureStartX) > 4) gestureMoved = true;
};

const finishGesture = () => {
  if (gestureMoved) suppressClickUntil = Date.now() + 500;
  gestureStartX = null;
  gestureMoved = false;
  removeGestureListeners();
};

const cancelGesture = () => {
  emit('cancelDrag');
  finishGesture();
};

removeGestureListeners = () => {
  document.removeEventListener('mousemove', trackGesture);
  document.removeEventListener('mouseup', finishGesture);
  document.removeEventListener('touchmove', trackGesture);
  document.removeEventListener('touchend', finishGesture);
  document.removeEventListener('touchcancel', cancelGesture);
};

const startDrag = event => {
  if (event.type === 'mousedown' && event.button !== 0) return;
  closeMenu();
  gestureStartX = clientX(event);
  gestureMoved = false;
  document.addEventListener('mousemove', trackGesture);
  document.addEventListener('mouseup', finishGesture);
  document.addEventListener('touchmove', trackGesture, { passive: true });
  document.addEventListener('touchend', finishGesture);
  document.addEventListener('touchcancel', cancelGesture);
  emit('startDrag', event);
};

const moveToLevel = index => {
  const boundedIndex = Math.max(
    0,
    Math.min(
      WORKSPACE_LIST_RESIZE_LEVELS.filter(
        level => level.width <= props.maxWidth
      ).length - 1,
      index
    )
  );
  selectWidth(WORKSPACE_LIST_RESIZE_LEVELS[boundedIndex].width);
};

const onTriggerKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    emit('cancelDrag');
    closeMenu({ restoreFocus: true });
    return;
  }

  const keyActions = {
    ArrowLeft: () => moveToLevel(currentLevelIndex.value - 1),
    ArrowDown: () => moveToLevel(currentLevelIndex.value - 1),
    ArrowRight: () => moveToLevel(currentLevelIndex.value + 1),
    ArrowUp: () => moveToLevel(currentLevelIndex.value + 1),
    Home: () => moveToLevel(0),
    End: () => moveToLevel(WORKSPACE_LIST_RESIZE_LEVELS.length - 1),
  };

  if (keyActions[event.key]) {
    event.preventDefault();
    keyActions[event.key]();
    return;
  }

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    toggleMenu();
  }
};

const onMenuKeydown = event => {
  if (event.key !== 'Escape') return;
  event.preventDefault();
  emit('cancelDrag');
  closeMenu({ restoreFocus: true });
};

const onDocumentPointerDown = event => {
  if (isMenuOpen.value && !rootRef.value?.contains(event.target)) closeMenu();
};

const onDocumentKeydown = event => {
  if (event.key !== 'Escape' || !props.isDragging || event.defaultPrevented) {
    return;
  }
  event.preventDefault();
  emit('cancelDrag');
  nextTick(() => triggerRef.value?.focus());
};

onMounted(() => {
  document.addEventListener('mousedown', onDocumentPointerDown);
  document.addEventListener('touchstart', onDocumentPointerDown);
  document.addEventListener('keydown', onDocumentKeydown);
});

onBeforeUnmount(() => {
  removeGestureListeners();
  document.removeEventListener('mousedown', onDocumentPointerDown);
  document.removeEventListener('touchstart', onDocumentPointerDown);
  document.removeEventListener('keydown', onDocumentKeydown);
});
</script>

<template>
  <div
    ref="rootRef"
    data-testid="conversation-workbench-list-gutter"
    class="group relative z-20 hidden h-full w-2 shrink-0 items-center justify-center md:flex"
  >
    <button
      ref="triggerRef"
      type="button"
      role="slider"
      :aria-label="t('WORKSPACE_RESIZE.LIST_ARIA_LABEL')"
      aria-valuemin="0"
      :aria-valuemax="
        WORKSPACE_LIST_RESIZE_LEVELS.filter(level => level.width <= maxWidth)
          .length - 1
      "
      :aria-valuenow="currentLevelIndex"
      :aria-valuetext="currentValueText"
      aria-haspopup="menu"
      :aria-expanded="isMenuOpen"
      aria-controls="workspace-list-resize-menu"
      class="absolute left-1/2 top-1/2 z-10 flex size-8 -translate-x-1/2 -translate-y-1/2 touch-none items-center justify-center rounded-xl border border-n-strong bg-n-solid-2 text-n-slate-10 shadow-sm outline-none transition-colors hover:border-n-brand hover:text-n-blue-11 focus-visible:border-n-brand focus-visible:text-n-blue-11 focus-visible:ring-2 focus-visible:ring-n-brand/30"
      :class="isDragging ? 'border-n-brand text-n-blue-11' : ''"
      @mousedown="startDrag"
      @touchstart="startDrag"
      @click="toggleMenu"
      @keydown="onTriggerKeydown"
    >
      <span class="i-ph-arrows-horizontal size-4" aria-hidden="true" />
    </button>

    <div
      v-if="!isMenuOpen"
      role="tooltip"
      class="pointer-events-none absolute left-full top-1/2 z-[60] w-52 -translate-y-1/2 rounded-xl border border-n-weak bg-n-solid-2 px-3 py-2 text-xs text-n-slate-11 opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
    >
      <strong class="block font-medium text-n-slate-12">
        {{ currentValueText }}
      </strong>
      <span>{{ t('WORKSPACE_RESIZE.DRAG_HINT') }}</span>
    </div>

    <div
      v-if="isMenuOpen"
      id="workspace-list-resize-menu"
      role="menu"
      :aria-label="t('WORKSPACE_RESIZE.MENU_LABEL')"
      class="absolute left-full top-1/2 z-[60] w-52 -translate-y-1/2 rounded-xl border border-n-weak bg-n-solid-2 p-1.5 shadow-lg"
      @keydown="onMenuKeydown"
    >
      <button
        v-for="(level, index) in WORKSPACE_LIST_RESIZE_LEVELS"
        :key="level.key"
        :ref="element => setFirstLevelRef(element, index)"
        type="button"
        role="menuitemradio"
        :disabled="level.width > maxWidth"
        :aria-checked="!isHidden && modelValue === level.width"
        class="flex min-h-11 w-full items-center justify-between disabled:cursor-not-allowed disabled:opacity-40 rounded-lg px-3 py-2 text-left text-sm text-n-slate-11 outline-none transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:bg-n-alpha-2 focus-visible:text-n-slate-12"
        :class="
          !isHidden && modelValue === level.width
            ? 'bg-n-alpha-2 font-medium text-n-slate-12'
            : ''
        "
        @click="selectWidth(level.width)"
      >
        <span>{{ levelLabel(level) }}</span>
        <span class="text-xs tabular-nums text-n-slate-10">
          {{ t('WORKSPACE_RESIZE.WIDTH_VALUE', { width: level.width }) }}
        </span>
      </button>

      <p v-if="maxWidth < 480" class="m-0 px-3 py-2 text-xs text-n-slate-11">
        {{ t('WORKSPACE_RESIZE.SPACE_HINT') }}
      </p>
      <div class="my-1 h-px bg-n-weak" />
      <button
        type="button"
        role="menuitem"
        class="flex min-h-11 w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-n-slate-11 outline-none transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:bg-n-alpha-2 focus-visible:text-n-slate-12"
        @click="hideList"
      >
        <span class="i-ph-eye-slash size-4" aria-hidden="true" />
        {{ t('WORKSPACE_RESIZE.HIDE') }}
      </button>
    </div>
  </div>
</template>
